import { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Select, InputNumber, Input, Button, Space, message, Empty, Typography, Divider, Tag } from 'antd';
import { ThunderboltOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/use_api';

const { Text, Paragraph } = Typography;

/**
 * Admin control for the Hangout Facilitator (roadmap Phase 6,
 * feature_hangout_facilitator): compose AI-orchestrated breakout rooms for a
 * context/relationship entity's hangout, or review/end the active one.
 *
 * `contextOptions` is the subset of the entity's connected entities that are
 * contexts — the composition rule picks people by cross-referencing these
 * (e.g. "1 from context A, 2 from context B"), same shape the backend expects
 * at POST /api/hangouts/{entityId}/breakout.
 */
function BreakoutPanel({ open, onCancel, entityId, contextOptions }) {
    const { user } = useAuth();
    const api = useApi();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [breakout, setBreakout] = useState(null); // active breakout, or null when none

    const loadActive = useCallback(() => {
        if (!user?.id) return;
        setFetching(true);
        api.get(`/api/hangouts/${entityId}/breakout`, { params: { user_id: user.id } })
            .then(setBreakout)
            .catch(err => setBreakout(err.status === 404 ? null : null))
            .finally(() => setFetching(false));
    }, [api, entityId, user?.id]);

    useEffect(() => {
        if (open) loadActive();
    }, [open, loadActive]);

    const handleStart = async () => {
        const values = await form.validateFields();
        setLoading(true);
        try {
            const created = await api.post(`/api/hangouts/${entityId}/breakout`, {
                user_id: user?.id,
                composition_rule: values.rules.map(r => ({ context_entity_id: r.context_entity_id, count: r.count })),
                room_size: values.room_size || undefined,
                topic: values.topic || undefined,
            });
            message.success(`Opened ${created.rooms.length} breakout room${created.rooms.length === 1 ? '' : 's'}`);
            setBreakout(created);
            form.resetFields();
        } catch (err) {
            message.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEnd = async () => {
        setLoading(true);
        try {
            await api.del(`/api/hangouts/${entityId}/breakout`, { params: { user_id: user?.id } });
            message.success('Breakout ended');
            setBreakout(null);
        } catch (err) {
            message.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={<Space><ThunderboltOutlined />AI Breakout Rooms</Space>}
            open={open}
            onCancel={onCancel}
            footer={null}
            width={520}
        >
            {fetching ? (
                <Paragraph type="secondary">Checking for an active breakout…</Paragraph>
            ) : breakout ? (
                <div>
                    <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                        {breakout.rooms.length} room{breakout.rooms.length === 1 ? '' : 's'} open
                        {breakout.topic ? ` — ${breakout.topic}` : ''}
                    </Paragraph>
                    {breakout.rooms.map(room => (
                        <div key={room.index} style={{ marginBottom: 12, padding: 10, border: '1px solid var(--border-color)', borderRadius: 6 }}>
                            <Space style={{ marginBottom: 6 }}>
                                <Tag>Room {room.index + 1}</Tag>
                                <Text type="secondary">{room.person_ids.length} participant{room.person_ids.length === 1 ? '' : 's'}</Text>
                            </Space>
                            <Paragraph style={{ marginBottom: 0, fontSize: 'var(--fs-sm)' }}>{room.facilitation_prompt}</Paragraph>
                        </div>
                    ))}
                    <Button danger icon={<DeleteOutlined />} loading={loading} onClick={handleEnd} block>
                        End breakout
                    </Button>
                </div>
            ) : (
                <Form form={form} layout="vertical" initialValues={{ rules: [{ count: 1 }] }}>
                    <Paragraph type="secondary">
                        Split this hangout into AI-facilitated breakout rooms. Each rule says
                        how many people from a given context join every room.
                    </Paragraph>
                    <Form.List name="rules">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...rest }) => (
                                    <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }} wrap>
                                        <Form.Item
                                            {...rest}
                                            name={[name, 'context_entity_id']}
                                            rules={[{ required: true, message: 'Pick a context' }]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Select
                                                placeholder="Context"
                                                style={{ width: 200 }}
                                                options={(contextOptions || []).map(c => ({ label: c.name, value: c.id }))}
                                                showSearch
                                                optionFilterProp="label"
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            {...rest}
                                            name={[name, 'count']}
                                            rules={[{ required: true, message: 'Count' }]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <InputNumber min={1} max={20} placeholder="Count" style={{ width: 90 }} />
                                        </Form.Item>
                                        {fields.length > 1 && (
                                            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                                        )}
                                    </Space>
                                ))}
                                <Button type="dashed" onClick={() => add({ count: 1 })} icon={<PlusOutlined />} style={{ marginBottom: 16 }}>
                                    Add rule
                                </Button>
                            </>
                        )}
                    </Form.List>
                    {!contextOptions?.length && (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="No connected contexts to compose rooms from yet"
                            style={{ marginBottom: 16 }}
                        />
                    )}
                    <Divider style={{ margin: '8px 0 16px' }} />
                    <Form.Item label="Max people per room (optional)" name="room_size" style={{ marginBottom: 12 }}>
                        <InputNumber min={2} max={50} style={{ width: '100%' }} placeholder="Leave blank to size rooms from the rules above" />
                    </Form.Item>
                    <Form.Item label="Topic (optional)" name="topic" style={{ marginBottom: 16 }}>
                        <Input placeholder="e.g. Getting to know each other" maxLength={120} />
                    </Form.Item>
                    <Button type="primary" icon={<ThunderboltOutlined />} loading={loading} onClick={handleStart} block>
                        Open breakout rooms
                    </Button>
                </Form>
            )}
        </Modal>
    );
}

export default BreakoutPanel;
